import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPageBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
  getModalStyles,
} from "../../utils/themeUtils";
import { captureError } from "../../utils/sentry";
import { isMemberRole } from "../../utils/roles";
import MediaUploadsPanel from "./MediaUploadsPanel";
import { useConfirmModal } from "../../hooks/useConfirmModal";

// The bundled system images (webpack require.context, in its own module so
// tests can replace it).
import systemImagesContext from "./systemImagesContext";
import { Button } from "../ui";

interface MediaLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (imagePath: string) => void;
  selectedImage?: string;
  parentName?: string;
}

interface SystemImage {
  name: string;
  path: string;
  preview: string;
  folder: string;
}

interface ImageFolder {
  name: string;
  displayName: string;
  images: SystemImage[];
}

// Load system images from the pre-loaded webpack context
const loadSystemImages = (): ImageFolder[] => {
  const folders: ImageFolder[] = [];

  try {
    const imagesByFolder: Record<string, SystemImage[]> = {};

    systemImagesContext.keys().forEach((key: string) => {
      // key format: ./folderName/imageName.jpg
      const parts = key.split("/");
      if (parts.length >= 3) {
        const folderName = parts[1];
        const imageName = parts[parts.length - 1];

        if (!imagesByFolder[folderName]) {
          imagesByFolder[folderName] = [];
        }

        imagesByFolder[folderName].push({
          name: imageName,
          path: key,
          preview: systemImagesContext(key),
          folder: folderName,
        });
      }
    });

    // Convert to ImageFolder array
    Object.entries(imagesByFolder).forEach(([folderName, images]) => {
      if (images.length > 0) {
        folders.push({
          name: folderName,
          displayName: folderName.charAt(0).toUpperCase() + folderName.slice(1),
          images,
        });
      }
    });
  } catch (error) {
    captureError(error, { component: "MediaLibrary", operation: "loadSystemImages" });
  }

  return folders;
};

const MediaLibrary: React.FC<MediaLibraryProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedImage,
  parentName,
}) => {
  const [activeTab, setActiveTab] = useState<"uploads" | "system">("system");
  const [systemImageFolders, setSystemImageFolders] = useState<ImageFolder[]>([]);
  const [selectedSystemFolder, setSelectedSystemFolder] = useState<string>("");
  const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
  const [folders, setFolders] = useState<string[]>([
    "programs",
    "emails",
    "logos",
  ]);

  // Map raw storage folder paths to user-friendly display names.
  // The storage path stays as-is (user_[uuid]) — only the label changes.
  const getFolderDisplayName = (folder: string): string => {
    const segment = folder.split("/").pop() || folder;
    // Current user's personal folder → "My Uploads"
    if (userDetails?.id && segment === `user_${userDetails.id}`) {
      return "My Uploads";
    }
    // Any other user_* folder (admin browsing another user's files)
    if (segment.startsWith("user_")) {
      return "User Uploads";
    }
    // System folders — capitalize
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  };
  const [selectedFolder, setSelectedFolder] = useState<any>("");
  const [, setUploadFile] = useState<any | null>(null);

  const { isDarkMode } = useTheme();
  const { confirmModal } = useConfirmModal();

  // Load system images from bundled assets on mount
  useEffect(() => {
    const folders = loadSystemImages();
    setSystemImageFolders(folders);
    if (folders.length > 0) {
      setSelectedSystemFolder(folders[0].name);
    }
  }, []);

  // No storage request here. This component is mounted CLOSED by
  // SideNavbarLayout on every staff page (Reporting > Revenue, athlete pages,
  // ...), so a list call on mount ran on every page load, and its failure
  // toast "Failed to load media files" reached staff who never opened the
  // library (PM c33e9974). MediaUploadsPanel is the one place uploads are
  // listed, and it only mounts once someone opens the Uploads tab.

  useEffect(() => {
    setUploadFile("");
  }, [activeTab]);

  useEffect(() => {
    if (!userDetails) return;

    // Member-portal roles (member/customer) see only their personal folder;
    // the else branch lists the staff system folders. Always
    // personal/user_<id>: the backend lets a family touch nothing else in the
    // media bucket (PM 65d0edd2). It used to prefer `parentName` or the
    // username, folders a member's upload would now be refused in.
    if (isMemberRole(userDetails.role)) {
      const personalFolder = `personal/user_${userDetails?.id}`;
      setFolders([personalFolder]);
      setSelectedFolder(personalFolder);
    } else {
      // Admins or staff can see all system folders
      setFolders([
        "programs",
        "emails",
        "logos",
        `personal/user_${userDetails?.id}`,
      ]);
      // setSelectedFolder("programs");
    }
  }, []);

  const handelClose = () => {
    onClose();
    setUploadFile("");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {confirmModal}
      <div
        className={`${getModalStyles(
          isDarkMode
        )} rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${getLightBorder(
            isDarkMode
          )}`}
        >
          <h2 className={`text-xl font-semibold ${getPrimaryText(isDarkMode)}`}>
            Media Library
          </h2>
          <Button
            type="button"
            variant="neutral"
            size="sm"
            iconOnly
            aria-label="Close"
            onClick={handelClose}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${getLightBorder(isDarkMode)}`}>
          <button
            onClick={() => setActiveTab("system")}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === "system"
                ? "text-blue-600 border-b-2 border-blue-600"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            System Images
          </button>
          <button
            onClick={() => setActiveTab("uploads")}
            className={`px-6 py-3 font-medium text-sm transition-colors ${
              activeTab === "uploads"
                ? "text-blue-600 border-b-2 border-blue-600"
                : isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Uploads
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === "uploads" ? (
            <MediaUploadsPanel
              folders={folders}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              onSelect={onSelect}
              selectedImage={selectedImage}
              parentName={parentName}
              isDarkMode={isDarkMode}
              getFolderDisplayName={getFolderDisplayName}
              userDetails={userDetails}
            />
          ) : systemImageFolders.length > 0 ? (
            <div className="flex h-full">
              {/* Folder Sidebar */}
              <div
                className={`w-48 border-r ${getLightBorder(
                  isDarkMode
                )} ${getPageBackground(isDarkMode)}`}
              >
                <div className="p-4">
                  <h4
                    className={`text-sm font-semibold ${getSecondaryText(
                      isDarkMode
                    )} mb-3`}
                  >
                    Folders
                  </h4>
                  <div className="space-y-1">
                    {systemImageFolders.map((folder) => (
                      <button
                        key={folder.name}
                        onClick={() => setSelectedSystemFolder(folder.name)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                          selectedSystemFolder === folder.name
                            ? "bg-blue-100 text-blue-700 font-medium"
                            : isDarkMode
                            ? "text-gray-400 hover:bg-gray-700"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                      >
                        <svg
                          className="w-4 h-4 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                          />
                        </svg>
                        <span className="truncate">{folder.displayName}</span>
                        <span
                          className={`ml-auto text-xs ${
                            isDarkMode
                              ? "bg-gray-600 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          } px-1.5 py-0.5 rounded-full`}
                        >
                          {folder.images.length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Images Grid */}
              <div className="flex-1 p-6">
                {selectedSystemFolder && (
                  <>
                    {(() => {
                      const currentFolder = systemImageFolders.find(
                        (f) => f.name === selectedSystemFolder
                      );
                      const currentImages = currentFolder?.images || [];

                      return (
                        <>
                          <div className="mb-4">
                            <h4
                              className={`text-lg font-semibold ${getPrimaryText(
                                isDarkMode
                              )}`}
                            >
                              {currentFolder?.displayName} Images
                            </h4>
                            <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                              {currentImages.length} image(s)
                            </p>
                          </div>

                          {currentImages.length === 0 ? (
                            <div className="text-center py-8">
                              <svg
                                className={`w-12 h-12 mx-auto ${
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                } mb-3`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                              </svg>
                              <p className={`${getMutedText(isDarkMode)}`}>
                                No images in this folder
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                              {currentImages.map((image, index) => {
                                const imageUrl = image.preview;
                                return (
                                  <div
                                    key={index}
                                    onClick={() => onSelect(imageUrl)}
                                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all hover:scale-105 hover:shadow-md ${
                                      selectedImage === imageUrl
                                        ? "border-blue-500 ring-2 ring-blue-200"
                                        : isDarkMode
                                        ? "border-gray-600 hover:border-gray-500"
                                        : "border-gray-200 hover:border-gray-300"
                                    }`}
                                  >
                                    <div
                                      className={`aspect-square ${
                                        isDarkMode ? "bg-gray-700" : "bg-gray-100"
                                      } flex items-center justify-center`}
                                    >
                                      <img
                                        src={imageUrl}
                                        alt={image.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = "none";
                                          const nextSibling = target.nextSibling as HTMLElement;
                                          if (nextSibling) {
                                            nextSibling.style.display = "flex";
                                          }
                                        }}
                                      />
                                      <div
                                        className={`w-full h-full ${
                                          isDarkMode
                                            ? "bg-gradient-to-br from-gray-600 to-gray-700"
                                            : "bg-gradient-to-br from-gray-200 to-gray-300"
                                        } items-center justify-center hidden`}
                                      >
                                        <svg
                                          className={`w-8 h-8 ${
                                            isDarkMode ? "text-gray-500" : "text-gray-400"
                                          }`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                          />
                                        </svg>
                                      </div>
                                    </div>
                                    <div className={`p-2 ${getCardBackground(isDarkMode)}`}>
                                      <p
                                        className={`text-xs ${getSecondaryText(
                                          isDarkMode
                                        )} truncate font-medium`}
                                      >
                                        {image.name}
                                      </p>
                                    </div>
                                    {selectedImage === imageUrl && (
                                      <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                        <svg
                                          className="w-3 h-3 text-white"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 px-6">
              <div
                className={`mx-auto w-16 h-16 ${
                  isDarkMode ? "bg-gray-700" : "bg-gray-100"
                } rounded-full flex items-center justify-center mb-4`}
              >
                <svg
                  className={`w-8 h-8 ${
                    isDarkMode ? "text-gray-300" : "text-gray-400"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3
                className={`text-lg font-medium ${getPrimaryText(
                  isDarkMode
                )} mb-2`}
              >
                No System Images
              </h3>
              <p className={`${getSecondaryText(isDarkMode)}`}>
                Add image files to the src/assets/system-images/ folder to see
                them here.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`flex items-center justify-end gap-3 p-6 border-t ${getLightBorder(
            isDarkMode
          )}`}
        >
          <button
            onClick={handelClose}
            className={`px-4 py-2 text-sm font-medium ${
              isDarkMode
                ? "text-gray-300 bg-gray-700 hover:bg-gray-600"
                : "text-gray-700 bg-gray-100 hover:bg-gray-200"
            } rounded-md transition-colors`}
          >
            Cancel
          </button>
          <button
            onClick={() => selectedImage && onSelect(selectedImage)}
            disabled={!selectedImage}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Select Image
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;
